import {Subject} from "rxjs";
import {IDurationAndSeekOffsetSecondsChanged, NoteProgressChangedTypes, NotesEventTypes} from "./NotesEventTypes";
import Note from "../../../models/soundslice/Note";
import Segment from "../../../models/view/lessonPage/Segment";
import {logger} from "../../log/logger";
import {NormalizedMidiInput} from "../../../models/NormalizedMidiInput";

const notesEventSubject = new Subject<NotesEventTypes>();
const notesProgressEventSubject = new Subject<NoteProgressChangedTypes>();
const notesDurationAndSeekOffsetSubject = new Subject<IDurationAndSeekOffsetSecondsChanged>();

export function subscribeToNotesEvents(callback: (message: NotesEventTypes) => void){
  const protectedCallback = (message: NotesEventTypes) => {
    try{
      callback(message);
    }catch(e){
      logger.lesson.error({message: `error encountered in observer of NotesEventTypes `, error: e as Error});
    }
  };
  const subscription = notesEventSubject.subscribe(protectedCallback);
  return subscription.unsubscribe.bind(subscription);
}

export function subscribeToNotesProgressEvents(callback: (message: NoteProgressChangedTypes) => void){
  const protectedCallback = (message: NoteProgressChangedTypes) => {
    try{
      callback(message);
    }catch(e){
      logger.lesson.error({message: `error encountered in observer of NoteProgressChangedTypes `, error: e as Error});
    }
  };
  const subscription = notesProgressEventSubject.subscribe(protectedCallback);
  return subscription.unsubscribe.bind(subscription);
}

export function subscribeToNotesDurationAndSeekOffsetChangedEvents(callback: (message: IDurationAndSeekOffsetSecondsChanged) => void){
  const protectedCallback = (message: IDurationAndSeekOffsetSecondsChanged) => {
    try{
      callback(message);
    }catch(e){
      logger.lesson.error({message: `error encountered in observer of IDurationAndSeekOffsetSecondsChanged `, error: e as Error});
    }
  };
  const subscription = notesDurationAndSeekOffsetSubject.subscribe(protectedCallback);
  return subscription.unsubscribe.bind(subscription);
}

function putEventOnBus(message: NotesEventTypes){
  notesEventSubject.next(message);
}

export function emitSheetMusicNotesCurrentlyBeingPlayed_changed(sheetMusicNotesCurrentlyBeingPlayed: Note[]){
  putEventOnBus({type: "sheetMusicNotesCurrentlyBeingPlayedChanged", sheetMusicNotesCurrentlyBeingPlayed});
}

export function emitNotesCurrentlySelected_changed(notesCurrentlySelected: Note[]){
  putEventOnBus({type: "notesCurrentlySelectedChanged", notesCurrentlySelected});
}

export function emitProgress_changed(progressPercent: number, seekOffset: number, durationSeconds: number){
  notesProgressEventSubject.next({type: "progressChanged", progressPercent, seekOffset, durationSeconds});
}

export function emitDurationAndSeekOffsetSeconds_changed(duration: number, seekOffset: number){
  notesDurationAndSeekOffsetSubject.next({type: "durationAndSeekOffsetSecondsChanged", duration, seekOffset});
}

export function emitPlayback_started(
  segment: Segment,
  notesInSegment: Note[],
  speed: number,
  speedRelativeToRecordingIndex: number,
  seekOffsetSeconds: number,
  loopEndOffsetSeconds: number,
  shouldClearScoredNotes: boolean
){
  logger.note.info({message: "emitPlayback_started"});
  putEventOnBus({
    type: "playbackStarted",
    segment,
    notesInSegment,
    speed,
    seekOffsetSeconds,
    loopEndOffsetSeconds,
    shouldClearScoredNotes,
    speedRelativeToRecordingIndex
  });
}

export function emitPlayerNote_pressed(midiNumber: number, velocity: number, receivedTime: number, source: string, deviceId: string, normalizedMidiInput: NormalizedMidiInput | null){
  putEventOnBus({type: "playerNotePressed", midiNumber, velocity, receivedTime, source, deviceId, normalizedMidiInput});
}

export function emitPlayerNote_released(midiNumber: number, receivedTime: number, source: string, deviceId: string, normalizedMidiInput: NormalizedMidiInput | null){
  putEventOnBus({type: "playerNoteReleased", midiNumber, receivedTime, source, deviceId, normalizedMidiInput});
}

export function emitUiKeyboardNote_pressed(midiNumber: number){
  putEventOnBus({type: "uiKeyboardNotePressed", midiNumber});
}

export function emitUiKeyboardNote_released(midiNumber: number){
  putEventOnBus({type: "uiKeyboardNoteReleased", midiNumber});
}

export function emitPlayback_ended(waitForScoringToComplete: boolean, shouldScore: boolean = true){
  logger.pitchDetection.info({
    message: "emitPlayback_ended",
    showStack: true,
    data: [
      { key: "waitForScoringToComplete", value: String(waitForScoringToComplete) },
      { key: "shouldScore", value: String(shouldScore) }
    ]
  });

  putEventOnBus({type: "playback_ended", waitForScoringToComplete, shouldScore});
}

export function emitRecording_started() {
  console.log("EVENT: recordingStarted");
  putEventOnBus({type: "recordingStarted"});
}

export function clearAllNoteFeedback(){
  putEventOnBus({type: "clearAllNoteFeedback"});
}

export function seekSkipBack(){
  putEventOnBus({type: "seekSkipBack"});
}

export function seekSkipForward(){
  putEventOnBus({type: "seekSkipForward"});
}
